import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, ChevronRight, MessageCircle } from 'lucide-react';
import { inboxFetchLimit, inboxPreview } from '../../utils/inboxPreview';
import userInboxService, { InboxItem, InboxList, markItemsRead } from '../../services/userInboxService';
import { pushTargetFor } from '../../utils/pushRoute';
import { relativeTimeParts } from '../../utils/activityStream';
import { useAuth } from '../../contexts/AuthContext';
import { appAlert } from '../alerts/appAlert';
import { t } from '../../i18n';

const TEAL = '#3fced1';

/** Relative Zeit („vor 2 Stunden“) über die vorhandenen Aktivitäten-Keys */
const relativeTime = (createdAt: number | null): string => {
  if (!createdAt) return '';
  const { key, value } = relativeTimeParts(createdAt);
  return t(`web.component.home-activity-list.${key}`, { count: value });
};

/** PURE: Zielroute eines Eintrags – ohne Ziel zum Profil */
export const inboxItemRoute = (item: InboxItem): string => pushTargetFor(item.data)?.route ?? '/profil';

/** Auth optional: ohne Provider (HomePage-Tests) oder gemocktes Modul → Gast, nie werfen */
const useOptionalAuth = (): { isAuthenticated: boolean } => {
  try {
    return useAuth();
  } catch {
    return { isAuthenticated: false };
  }
};

interface Props {
  /** Profil-Tab „Benachrichtigungen“: ohne Nachrichten-Zeile, mit eigener Überschrift */
  embedded?: boolean;
  /** Änderung → neu laden (z. B. Pull-to-Refresh / Tab-Wechsel) */
  refreshKey?: number;
  /** Nach einer Änderung des Ungelesen-Zählers (für Badges) */
  onUnreadChange?: (unreadCount: number) => void;
}

/**
 * „Neu für dich“ (Marcel 2026-10-24): oben im Tab „Für dich“ und im Profil → Benachrichtigungen.
 * Zeile „Nachrichten“ (ungelesene Direkt-/Listing-Chats), darunter alles Persönliche aus dem
 * Posteingang (Ranglisten-Anfragen, Anmeldungen, Mitgliedschaften, Follower, …). Klick markiert den
 * Eintrag als gelesen und öffnet die passende Seite (gleiche Routentabelle wie die App).
 * Flach: Trennlinien statt Karten, Türkis nur als Akzent. Gäste sehen nichts.
 */
const ForYouInbox: React.FC<Props> = ({ embedded = false, refreshKey = 0, onUnreadChange }) => {
  const { isAuthenticated } = useOptionalAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<InboxList | null>(null);
  const [busy, setBusy] = useState(false);
  // Vollbild „Alles anzeigen“ (25.10.2026, wie die App): eigene Liste mit allen Einträgen
  const [fullOpen, setFullOpen] = useState(false);
  const [fullData, setFullData] = useState<InboxList | null>(null);
  useEffect(() => {
    if (!fullOpen) return undefined;
    let cancelled = false;
    userInboxService.list({ limit: 30 }).then((r) => { if (!cancelled) setFullData(r); }).catch(() => { if (!cancelled) setFullData({ items: [], unreadCount: 0, chatUnreadCount: 0 } as InboxList); });
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { cancelled = true; document.body.style.overflow = prev; };
  }, [fullOpen]);
  const closeFull = () => { setFullOpen(false); setFullData(null); void load(); };

  const load = useCallback(async () => {
    if (!isAuthenticated) { setData(null); return; }
    try {
      // 25.10.2026: Vorschau im Tab „Für dich“ nur 7 (6 sichtbar + Prüfung „gibt es mehr?“), Profil 30
      const result = await userInboxService.list({ limit: inboxFetchLimit({ embedded }) });
      setData(result);
      onUnreadChange?.(result.unreadCount);
    } catch {
      /* fail-soft: Posteingang ist Zusatz, kein Fehler-Popup beim Laden */
    }
  }, [isAuthenticated, onUnreadChange, embedded]);

  useEffect(() => { void load(); }, [load, refreshKey]);
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') void load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  const openItem = async (item: InboxItem) => {
    const route = inboxItemRoute(item);
    if (item.unread) {
      setData((prev) => (prev ? { ...prev, items: markItemsRead(prev.items, [item.id]), unreadCount: Math.max(0, prev.unreadCount - 1) } : prev));
      setFullData((prev) => (prev ? { ...prev, items: markItemsRead(prev.items, [item.id]), unreadCount: Math.max(0, prev.unreadCount - 1) } : prev));
      onUnreadChange?.(Math.max(0, (data?.unreadCount ?? 1) - 1));
      userInboxService.markRead([item.id]).catch(() => undefined);
    }
    if (fullOpen) { setFullOpen(false); setFullData(null); }
    navigate(route);
  };

  const renderItems = (items: InboxItem[], testId: string) => (
    <ul className="divide-y divide-zinc-800 border-t border-zinc-800" data-testid={testId}>
      {items.map((item) => (
        <li key={item.id}>
          <button type="button" onClick={() => openItem(item)} className="flex w-full items-start gap-3 py-3 text-left transition hover:bg-white/5" data-testid={`${testId === 'for-you-inbox-list' ? 'for-you-inbox-item' : 'for-you-inbox-full-item'}-${item.id}`} data-unread={item.unread ? 'true' : 'false'}>
            <span className="mt-2 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: item.unread ? TEAL : 'transparent' }} aria-hidden="true" />
            <span className="min-w-0 flex-1">
              {item.title && <span className={`block truncate text-sm ${item.unread ? 'font-semibold text-white' : 'font-medium text-zinc-300'}`}>{item.title}</span>}
              {item.body && <span className={`block text-sm ${item.title ? 'text-zinc-400' : item.unread ? 'font-semibold text-white' : 'text-zinc-300'}`}>{item.body}</span>}
              <span className="mt-0.5 block text-[11px] text-zinc-500">{relativeTime(item.createdAt)}</span>
            </span>
            <ChevronRight size={16} className="mt-1 shrink-0 text-zinc-600" aria-hidden="true" />
          </button>
        </li>
      ))}
    </ul>
  );

  const markAll = async () => {
    if (!data || busy) return;
    setBusy(true);
    try {
      await userInboxService.markRead('all');
      setData((prev) => (prev ? { ...prev, items: markItemsRead(prev.items, 'all'), unreadCount: 0 } : prev));
      setFullData((prev) => (prev ? { ...prev, items: markItemsRead(prev.items, 'all'), unreadCount: 0 } : prev));
      onUnreadChange?.(0);
    } catch (err) {
      appAlert.error(userInboxService.getErrorMessage(err, t('web.component.for-you-inbox.gelesen-fehlgeschlagen')));
    } finally {
      setBusy(false);
    }
  };

  if (!isAuthenticated || !data) return null;

  const hasChat = data.chatUnreadCount > 0;
  const hasItems = data.items.length > 0;
  const preview = inboxPreview(data.items.length, { embedded });
  const shownItems = data.items.slice(0, preview.visibleCount);

  return (
    <section className={`${embedded ? '' : 'mb-8'} border-b border-zinc-800 pb-4`} data-testid="for-you-inbox" aria-label={t('web.component.for-you-inbox.neu-fuer-dich')}>
      <div className="flex items-center justify-between gap-3 pb-2">
        <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em]" style={{ color: TEAL }}>
          <Bell size={14} aria-hidden="true" /> {t('web.component.for-you-inbox.neu-fuer-dich')}
          {data.unreadCount > 0 && (
            <span className="rounded-full px-1.5 py-0.5 text-[10px] font-bold text-black" style={{ backgroundColor: TEAL }} data-testid="for-you-inbox-unread">{data.unreadCount}</span>
          )}
        </h2>
        {data.unreadCount > 0 && (
          <button type="button" onClick={markAll} disabled={busy} className="text-xs font-semibold text-zinc-400 transition hover:text-white disabled:opacity-50" data-testid="for-you-inbox-mark-all">
            {t('web.component.for-you-inbox.alle-gelesen')}
          </button>
        )}
      </div>

      {!embedded && (
        <Link to="/nachrichten" className="flex items-center gap-3 border-t border-zinc-800 py-3 transition hover:bg-white/5" data-testid="for-you-inbox-chats">
          <MessageCircle size={18} style={{ color: hasChat ? TEAL : undefined }} className={hasChat ? '' : 'text-zinc-500'} aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-white">{t('web.component.for-you-inbox.nachrichten')}</span>
            <span className="block text-xs text-zinc-400">
              {hasChat ? t('web.component.for-you-inbox.ungelesene-nachrichten', { count: data.chatUnreadCount }) : t('web.component.for-you-inbox.keine-ungelesenen-nachrichten')}
            </span>
          </span>
          {hasChat && <span className="rounded-full px-2 py-0.5 text-[11px] font-bold text-black" style={{ backgroundColor: TEAL }} data-testid="for-you-inbox-chat-count">{data.chatUnreadCount}</span>}
          <ChevronRight size={16} className="text-zinc-600" aria-hidden="true" />
        </Link>
      )}

      {hasItems ? renderItems(shownItems, 'for-you-inbox-list') : (
        <p className="border-t border-zinc-800 py-3 text-sm text-zinc-500" data-testid="for-you-inbox-empty">{t('web.component.for-you-inbox.nichts-neues')}</p>
      )}

      {/* Marcel 25.10.2026: Rest im Vollbild, damit der Feed darunter sichtbar bleibt (wie die App) */}
      {preview.showAll && (
        <button type="button" onClick={() => setFullOpen(true)} className="mt-1 inline-flex items-center gap-1 border-t border-zinc-800 py-3 text-sm font-semibold" style={{ color: TEAL }} data-testid="for-you-inbox-show-all">
          {t('web.component.for-you-inbox.alles-anzeigen')} <ChevronRight size={16} aria-hidden="true" />
        </button>
      )}

      {fullOpen && createPortal(
        <div className="fixed inset-0 z-[200] flex flex-col bg-black text-white" role="dialog" aria-modal="true" aria-label={t('web.component.for-you-inbox.neu-fuer-dich')} data-testid="for-you-inbox-fullscreen">
          <header className="flex items-center gap-3 border-b border-zinc-900 px-4 py-3">
            <button type="button" onClick={closeFull} aria-label={t('web.component.chats-manager.zurueck')} className="flex h-9 w-9 items-center justify-center rounded-full text-zinc-300 transition-colors hover:bg-zinc-900 hover:text-white" data-testid="for-you-inbox-fullscreen-close"><ArrowLeft size={20} /></button>
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-base font-bold">{t('web.component.for-you-inbox.neu-fuer-dich')}</h2>
              {fullData && fullData.unreadCount > 0 && <p className="truncate text-xs text-zinc-500">{fullData.unreadCount} {t('web.component.for-you-inbox.ungelesen-kurz')}</p>}
            </div>
            {fullData && fullData.unreadCount > 0 && (
              <button type="button" onClick={markAll} disabled={busy} className="text-xs font-semibold text-zinc-400 transition hover:text-white disabled:opacity-50">{t('web.component.for-you-inbox.alle-gelesen')}</button>
            )}
          </header>
          <div className="flex-1 overflow-y-auto px-4 pb-8">
            {fullData === null ? (
              <p className="py-6 text-sm text-zinc-500">…</p>
            ) : fullData.items.length > 0 ? renderItems(fullData.items, 'for-you-inbox-full-list') : (
              <p className="py-6 text-sm text-zinc-500">{t('web.component.for-you-inbox.nichts-neues')}</p>
            )}
          </div>
        </div>,
        document.body,
      )}
    </section>
  );
};

export default ForYouInbox;
